import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, ArrowLeft, CheckCircle2, Loader2 } from 'lucide-react';
import API from '../../api/api';
import styles from './ServiceComingSoon.module.css';
import { useRevealOnScroll } from './useRevealOnScroll';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface ServiceFeature {
  icon: React.ReactNode;
  title: string;
  description: string;
}

export interface AudienceOption {
  label: string;
  value: string;
}

interface FieldErrors {
  email?: string;
  audience?: string;
}

interface ServiceComingSoonProps {
  serviceId: string;
  accent: string;
  accentDark: string;
  accentSoft: string;
  logo: string;
  logoAlt: string;
  heroImage?: string;
  heroImageAlt?: string;
  kicker: string;
  title: string;
  subtitle: string;
  description: string;
  features: ServiceFeature[];
  audienceQuestion: string;
  audienceOptions: AudienceOption[];
}

const FeatureCard: React.FC<{ feature: ServiceFeature; index: number }> = ({ feature, index }) => {
  const { ref, visible } = useRevealOnScroll<HTMLDivElement>();
  return (
    <div
      ref={ref}
      className={`${styles.featureCard} ${visible ? styles.featureCardVisible : ''}`}
      style={{ transitionDelay: `${index * 90}ms` }}
    >
      <span className={styles.featureIcon}>{feature.icon}</span>
      <h3 className={styles.featureTitle}>{feature.title}</h3>
      <p className={styles.featureDesc}>{feature.description}</p>
    </div>
  );
};

const ServiceComingSoon: React.FC<ServiceComingSoonProps> = ({
  serviceId,
  accent,
  accentDark,
  accentSoft,
  logo,
  logoAlt,
  heroImage,
  heroImageAlt,
  kicker,
  title,
  subtitle,
  description,
  features,
  audienceQuestion,
  audienceOptions,
}) => {
  const [audience, setAudience] = useState(audienceOptions[0]?.value ?? '');
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const { ref: formRef, visible: formVisible } = useRevealOnScroll<HTMLDivElement>(0.1);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    const trimmedEmail = email.trim();
    const nextFieldErrors: FieldErrors = {};

    if (!trimmedEmail) {
      nextFieldErrors.email = 'Please enter your email address.';
    } else if (!EMAIL_REGEX.test(trimmedEmail)) {
      nextFieldErrors.email = "That email address doesn't look right.";
    }

    if (!audience) {
      nextFieldErrors.audience = 'Please choose the option that best describes you.';
    }

    if (Object.keys(nextFieldErrors).length > 0) {
      setFieldErrors(nextFieldErrors);
      setFormError(null);
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSubmitting(true);

    try {
      await API.post('/service-interest', {
        service: serviceId,
        email: trimmedEmail,
        audience,
      });
      setAlreadyRegistered(false);
      setSubmitted(true);
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          const { status, message, field } = error.response.data ?? {};
          if (status === 409) {
            setAlreadyRegistered(true);
            setSubmitted(true);
          } else if (field === 'email' || field === 'audience') {
            setFieldErrors({ [field]: message || 'Please check this field and try again.' });
          } else {
            setFormError(message || 'Something went wrong on our end. Please try again.');
          }
        } else if (error.request) {
          setFormError("We couldn't reach the server. Check your connection and try again.");
        } else {
          setFormError('Something went wrong. Please try again.');
        }
      } else {
        setFormError('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const themeVars = {
    '--sc-accent': accent,
    '--sc-accent-dark': accentDark,
    '--sc-accent-soft': accentSoft,
  } as React.CSSProperties;

  return (
    <div className={styles.page} style={themeVars}>
      <header className={styles.topbar}>
        <Link to="/" className={styles.backLink}>
          <ArrowLeft size={16} strokeWidth={2.25} />
          All iGiTREE experiences
        </Link>
        <span className={styles.brandBadge}>
          <img src={logo} alt={logoAlt} className={styles.brandBadgeImg} />
        </span>
      </header>

      <section className={styles.hero} aria-label={heroImageAlt}>
        {heroImage ? (
          <div className={styles.heroPhoto} style={{ backgroundImage: `url(${heroImage})` }} aria-hidden />
        ) : (
          <div className={styles.heroFallbackBg} aria-hidden />
        )}
        <div className={styles.heroOverlay} aria-hidden />
        <div className={styles.heroInner}>
          <span className={styles.kicker}>{kicker}</span>
          <h1 className={styles.heroTitle}>{title}</h1>
          <p className={styles.heroSubtitle}>{subtitle}</p>
          <p className={styles.heroDesc}>{description}</p>
        </div>
        <div className={styles.heroScrollHint} aria-hidden>
          <span />
        </div>
      </section>

      <section className={styles.features}>
        <div className={styles.featuresGrid}>
          {features.map((feature, index) => (
            <FeatureCard feature={feature} index={index} key={feature.title} />
          ))}
        </div>
      </section>

      <section className={styles.interestSection}>
        <div
          ref={formRef}
          className={`${styles.interestCard} ${formVisible ? styles.interestCardVisible : ''}`}
        >
          {submitted ? (
            <div className={styles.successState}>
              <CheckCircle2 size={44} strokeWidth={1.75} className={styles.successIcon} />
              <h2 className={styles.successTitle}>
                {alreadyRegistered ? "You're already on the list" : "You're on the list!"}
              </h2>
              <p className={styles.successDesc}>
                {alreadyRegistered
                  ? "Good news — this email is already registered for updates. We'll reach out as soon as this experience launches."
                  : "Thanks for your interest — we'll reach out as soon as this experience launches."}
              </p>
            </div>
          ) : (
            <>
              <h2 className={styles.interestTitle}>Be Part of What's Next</h2>
              <p className={styles.interestQuestion}>{audienceQuestion}</p>
              <form className={styles.form} onSubmit={handleSubmit} noValidate>
                {formError && (
                  <div className={styles.formAlert} role="alert">
                    <AlertCircle size={18} strokeWidth={2} />
                    <span>{formError}</span>
                  </div>
                )}

                <div>
                  <div
                    className={styles.radioGroup}
                    role="radiogroup"
                    aria-label={audienceQuestion}
                    aria-invalid={Boolean(fieldErrors.audience)}
                  >
                    {audienceOptions.map((option) => (
                      <label
                        key={option.value}
                        className={`${styles.radioOption} ${audience === option.value ? styles.radioOptionActive : ''}`}
                      >
                        <input
                          type="radio"
                          name="audience"
                          value={option.value}
                          checked={audience === option.value}
                          onChange={() => {
                            setAudience(option.value);
                            setFieldErrors((prev) => ({ ...prev, audience: undefined }));
                          }}
                        />
                        <span className={styles.radioDot} aria-hidden />
                        {option.label}
                      </label>
                    ))}
                  </div>
                  {fieldErrors.audience && (
                    <p className={styles.fieldError} role="alert">
                      {fieldErrors.audience}
                    </p>
                  )}
                </div>

                <div>
                  <div className={styles.emailRow}>
                    <input
                      type="email"
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setFieldErrors((prev) => ({ ...prev, email: undefined }));
                      }}
                      className={`${styles.emailInput} ${fieldErrors.email ? styles.inputError : ''}`}
                      aria-label="Email address"
                      aria-invalid={Boolean(fieldErrors.email)}
                      disabled={isSubmitting}
                    />
                    <button type="submit" className={styles.submitBtn} disabled={isSubmitting}>
                      {isSubmitting ? (
                        <>
                          <Loader2 size={18} strokeWidth={2.5} className={styles.spinner} />
                          Registering…
                        </>
                      ) : (
                        'Register Your Interest'
                      )}
                    </button>
                  </div>
                  {fieldErrors.email && (
                    <p className={styles.fieldError} role="alert">
                      {fieldErrors.email}
                    </p>
                  )}
                </div>
              </form>
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default ServiceComingSoon;
